import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useCouponCompanyConfigDetail } from '@/core/domain/coupon/company/company.hooks';
import { useTaxCouponCreate } from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  CouponOriginEntity,
  TaxCouponItemType,
} from '@/core/domain/coupon/coupons/coupons.types';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { DirectSale } from '@/core/domain/sales/DirectSale/DirectSale.types';
import { Button, Col, Empty, Form, Modal, Row } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  directSale?: DirectSale;
}

const DirectSaleGenerateTaxCouponModal: React.FC<Props> = ({
  open,
  onClose,
  directSale,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const companyId = Form.useWatch('companyId', form);

  useEffect(() => {
    if (directSale && open) {
      form.setFieldValue('companyId', directSale.companyId);
    }
  }, [directSale, open]);

  const history = useHistory();

  const { data: matrix } = useMatrix();

  const { data: couponCompanyConfig } = useCouponCompanyConfigDetail(
    companyId,
    { refetchOnFocus: true },
  );

  const taxCouponCreate = useTaxCouponCreate();

  const customerDetail = useCustomerReduced();

  const hasActive =
    !!couponCompanyConfig?.defaultTaxConfig.active && !!companyId;

  async function handleGenerate(redirect = false) {
    try {
      if (directSale) {
        const customer = await customerDetail.mutateAsync(
          directSale.customerId,
        );

        if (customer) {
          const taxCoupon = await taxCouponCreate.mutateAsync({
            companyId: directSale.companyId,
            recipient: {
              clientName: customer.name,
              cpfCnpj: customer.cpfCnpj || '',
              id: customer.id,
            },
            couponItems: directSale.items.map((item) => ({
              type: TaxCouponItemType.PART,
              discount: item.discountCurrency || 0,
              unitValue: item.unitPrice,
              quantity: item.quantity,
              itemId: item.part.id,
              description: item.part.name,
            })),
            origins: [
              {
                entity: CouponOriginEntity.SALES,
                entityId: String(directSale.id),
                entityCode: directSale.saleCode,
              },
            ],
          });

          if (redirect && taxCoupon) {
            history.push(
              `/app/fiscal/cfe/${taxCoupon.id}/${directSale.companyId}/general`,
            );
          } else {
            handleClose();
          }
        }
      }
    } catch {}
  }

  function handleClose() {
    form.resetFields();
    onClose();
  }

  return (
    <Modal
      title="Gerar cupom fiscal"
      open={open}
      onCancel={handleClose}
      width={800}
      destroyOnClose
      footer={
        hasActive ? (
          <SpaceForButton justifyContent="flex-end">
            <Button
              block
              onClick={onClose}
              disabled={taxCouponCreate.isLoading}
            >
              Cancelar
            </Button>

            <Button
              block
              type="primary"
              onClick={() => handleGenerate()}
              disabled={taxCouponCreate.isLoading}
            >
              Somente criar cupom
            </Button>

            <Button
              block
              type="primary"
              onClick={() => handleGenerate(true)}
              disabled={taxCouponCreate.isLoading}
            >
              Criar e redirecionar para o cupom
            </Button>
          </SpaceForButton>
        ) : (
          false
        )
      }
    >
      <Form form={form} preserve={false}>
        <Row gutter={24}>
          <Col span={24}>
            <FloatingFormItem
              name="companyId"
              label={intl.formatMessage({ id: 'directSale.form.company' })}
            >
              <CompaniesInfiniteSelectField
                disabled={
                  !matrix?.matrixSettings.generateInvoiceDifferentCompanies
                }
              />
            </FloatingFormItem>
          </Col>

          {!hasActive && (
            <Empty
              style={{ width: '100%' }}
              description={
                <>
                  <p>
                    A empresa selecionada não tem configuração de cupom fiscal
                    ativada
                  </p>

                  <Button
                    onClick={() =>
                      window.open(
                        `/app/registry/companies/${companyId}/coupon`,
                        '_blank',
                      )
                    }
                  >
                    Configurar empresa
                  </Button>
                </>
              }
            />
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default DirectSaleGenerateTaxCouponModal;
